import { useState } from 'react';
import { orders as allOrders, statusLabels, productTypes, paymentStatusLabels, cargoStatusLabels } from '@/mocks/orders';
import type { Order } from '@/mocks/orders';

interface OrderFiltersProps {
  onFilter: (filtered: Order[]) => void;
}

export default function OrderFilters({ onFilter }: OrderFiltersProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [paymentFilter, setPaymentFilter] = useState<string>('');
  const [productTypeFilter, setProductTypeFilter] = useState<string>('');
  const [cargoFilter, setCargoFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [quickFilter, setQuickFilter] = useState<string>('');

  const applyFilters = (
    s: string, status: string, payment: string, product: string, cargo: string,
    from: string, to: string, quick: string
  ) => {
    let filtered = [...allOrders];

    if (s) {
      const q = s.toLowerCase();
      filtered = filtered.filter(o =>
        o.customerName.toLowerCase().includes(q) ||
        o.id.toLowerCase().includes(q) ||
        o.phone.includes(q) ||
        o.city.toLowerCase().includes(q) ||
        o.productName.toLowerCase().includes(q)
      );
    }

    if (status) filtered = filtered.filter(o => o.status === status);
    if (payment) filtered = filtered.filter(o => o.paymentStatus === payment);
    if (product) filtered = filtered.filter(o => o.productType === product);
    if (cargo) filtered = filtered.filter(o => o.cargoStatus === cargo);
    if (from) filtered = filtered.filter(o => o.orderDate >= from);
    if (to) filtered = filtered.filter(o => o.orderDate <= to);

    switch (quick) {
      case 'late': filtered = filtered.filter(o => o.isLate); break;
      case 'today': filtered = filtered.filter(o => o.orderDate === '2026-06-17'); break;
      case 'pending_payment': filtered = filtered.filter(o => o.paymentType === 'kapida_odeme' && o.paymentStatus === 'bekleniyor'); break;
    }

    onFilter(filtered);
  };

  const handleQuickFilter = (key: string) => {
    const next = quickFilter === key ? '' : key;
    setQuickFilter(next);
    applyFilters(search, statusFilter, paymentFilter, productTypeFilter, cargoFilter, dateFrom, dateTo, next);
  };

  const handleSearch = (val: string) => {
    setSearch(val);
    applyFilters(val, statusFilter, paymentFilter, productTypeFilter, cargoFilter, dateFrom, dateTo, quickFilter);
  };

  const handleStatus = (val: string) => {
    setStatusFilter(val);
    applyFilters(search, val, paymentFilter, productTypeFilter, cargoFilter, dateFrom, dateTo, quickFilter);
  };

  const handlePayment = (val: string) => {
    setPaymentFilter(val);
    applyFilters(search, statusFilter, val, productTypeFilter, cargoFilter, dateFrom, dateTo, quickFilter);
  };

  const handleProduct = (val: string) => {
    setProductTypeFilter(val);
    applyFilters(search, statusFilter, paymentFilter, val, cargoFilter, dateFrom, dateTo, quickFilter);
  };

  const handleCargo = (val: string) => {
    setCargoFilter(val);
    applyFilters(search, statusFilter, paymentFilter, productTypeFilter, cargoFilter, dateFrom, dateTo, quickFilter);
  };

  return (
    <div className="space-y-3">
      {/* Search & Quick Filters Row */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
          <input
            type="text"
            placeholder="Sipariş ID, müşteri adı, telefon veya şehir ile ara..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30 transition-all"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => handleQuickFilter('today')}
            className={`px-3 py-2 text-xs font-medium rounded-lg border transition-all whitespace-nowrap cursor-pointer ${
              quickFilter === 'today' ? 'bg-gold-500 text-white border-gold-500' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
            }`}
          >
            Bugün
          </button>
          <button
            onClick={() => handleQuickFilter('late')}
            className={`px-3 py-2 text-xs font-medium rounded-lg border transition-all whitespace-nowrap cursor-pointer ${
              quickFilter === 'late' ? 'bg-status-red text-white border-status-red' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
            }`}
          >
            Geciken İşler
          </button>
          <button
            onClick={() => handleQuickFilter('pending_payment')}
            className={`px-3 py-2 text-xs font-medium rounded-lg border transition-all whitespace-nowrap cursor-pointer ${
              quickFilter === 'pending_payment' ? 'bg-status-orange text-white border-status-orange' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
            }`}
          >
            Kapıda Ödeme Bekleyen
          </button>
        </div>
      </div>

      {/* Dropdown Filters */}
      <div className="flex flex-wrap gap-2">
        <select
          value={statusFilter}
          onChange={(e) => handleStatus(e.target.value)}
          className="px-3 py-1.5 bg-white border border-stone-200 rounded-lg text-xs text-stone-700 cursor-pointer focus:outline-none focus:border-gold-400"
        >
          <option value="">Tüm Durumlar</option>
          {Object.entries(statusLabels).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <select
          value={paymentFilter}
          onChange={(e) => handlePayment(e.target.value)}
          className="px-3 py-1.5 bg-white border border-stone-200 rounded-lg text-xs text-stone-700 cursor-pointer focus:outline-none focus:border-gold-400"
        >
          <option value="">Tüm Ödeme Durumları</option>
          {Object.entries(paymentStatusLabels).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <select
          value={productTypeFilter}
          onChange={(e) => handleProduct(e.target.value)}
          className="px-3 py-1.5 bg-white border border-stone-200 rounded-lg text-xs text-stone-700 cursor-pointer focus:outline-none focus:border-gold-400"
        >
          <option value="">Tüm Ürün Tipleri</option>
          {productTypes.map((pt) => (
            <option key={pt.value} value={pt.value}>{pt.label}</option>
          ))}
        </select>
        <select
          value={cargoFilter}
          onChange={(e) => handleCargo(e.target.value)}
          className="px-3 py-1.5 bg-white border border-stone-200 rounded-lg text-xs text-stone-700 cursor-pointer focus:outline-none focus:border-gold-400"
        >
          <option value="">Tüm Kargo Durumları</option>
          {Object.entries(cargoStatusLabels).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <div className="flex items-center gap-1.5">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); applyFilters(search, statusFilter, paymentFilter, productTypeFilter, cargoFilter, e.target.value, dateTo, quickFilter); }}
            className="px-3 py-1.5 bg-white border border-stone-200 rounded-lg text-xs text-stone-700 focus:outline-none focus:border-gold-400"
          />
          <span className="text-xs text-stone-400">-</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); applyFilters(search, statusFilter, paymentFilter, productTypeFilter, cargoFilter, dateFrom, e.target.value, quickFilter); }}
            className="px-3 py-1.5 bg-white border border-stone-200 rounded-lg text-xs text-stone-700 focus:outline-none focus:border-gold-400"
          />
        </div>
        {(statusFilter || paymentFilter || productTypeFilter || cargoFilter || dateFrom || dateTo || quickFilter) && (
          <button
            onClick={() => {
              setStatusFilter(''); setPaymentFilter(''); setProductTypeFilter(''); setCargoFilter('');
              setDateFrom(''); setDateTo(''); setQuickFilter('');
              applyFilters(search, '', '', '', '', '', '', '');
            }}
            className="px-3 py-1.5 text-xs text-stone-500 hover:text-stone-700 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-close-line mr-1" />Filtreleri Temizle
          </button>
        )}
      </div>
    </div>
  );
}